import React, { useState } from "react";
import { useWalletClient, usePublicClient } from "wagmi";
import { adminApi } from "../../services/api";
import { formatEnglishDate } from "../../utils/dateFormatting";
import AppleTreeABI from "../../contracts/AppleTree.json";
import { QUESTIONS_BY_ID } from "../../shared/esgQuestions";

/**
 * Componente per certificare una richiesta ESG on-chain
 * Flow:
 * 1. Call backend POST /admin/esg/certifications/prepare-proxy (secure proxy)
 * 2. Show confirmation dialog with certification details
 * 3. Sign on-chain tx: certifyCompany(companyWallet)
 * 4. Wait for transaction receipt
 * 5. Call backend POST /admin/esg/certifications/confirm with requestId and txHash
 * 6. Update UI state
 */
const AdminCertifyButton = ({ 
  requestId, 
  companyWalletAddress,
  adminKey, 
  walletAddress,
  onSuccess,
  onError 
}) => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  
  const [certifying, setCertifying] = useState(false);
  const [step, setStep] = useState("");
  const [certData, setCertData] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [scoringData, setScoringData] = useState(null);
  const [scoringStatus, setScoringStatus] = useState(null);
  const [backendScoringLoaded, setBackendScoringLoaded] = useState(false);
  const [backendScoringError, setBackendScoringError] = useState("");

  const normalizeScoreTotals = (source) => {
    if (!source || typeof source !== "object") return null;
    const E = Number(source.E ?? source.environmental ?? source.env ?? 0) || 0;
    const S = Number(source.S ?? source.social ?? source.soc ?? 0) || 0;
    const G = Number(source.G ?? source.governance ?? source.gov ?? 0) || 0;
    const total = Number(source.total ?? (E + S + G)) || 0;
    return { E, S, G, total };
  };

  const computeTotalsFromByQuestion = (byQuestion) => {
    if (!byQuestion || typeof byQuestion !== "object") return null;

    let E = 0;
    let S = 0;
    let G = 0;

    Object.entries(byQuestion).forEach(([key, value]) => {
      const score = Number(value?.score ?? 0) || 0;
      if (!score) return;

      const question = QUESTIONS_BY_ID.get(key);
      if (question?.section === "E") E += score;
      else if (question?.section === "S") S += score;
      else if (question?.section === "G") G += score;
      else if (typeof key === "string") {
        if (key.startsWith("E")) E += score;
        else if (key.startsWith("S")) S += score;
        else if (key.startsWith("G")) G += score;
      }
    });

    return { E, S, G, total: E + S + G };
  };

  const extractBestTotals = (source) => {
    if (!source || typeof source !== "object") return null;

    const fromTotals = normalizeScoreTotals(source.totals);
    if (fromTotals && (fromTotals.total > 0 || source.totals)) return fromTotals;

    const fromSummary = normalizeScoreTotals(source.scoresSummary);
    if (fromSummary && (fromSummary.total > 0 || source.scoresSummary)) return fromSummary;

    const fromByQuestion = computeTotalsFromByQuestion(source.byQuestion || source.scoring);
    if (fromByQuestion) return fromByQuestion;

    return null;
  };

  const extractScoringStatus = (source) => {
    return source?.scoringStatus || source?.status || null;
  };

  // Indirizzo del contratto AppleTree
  const APPLETREE_CONTRACT = process.env.REACT_APP_APPLETREE_CONTRACT || "0x58665Cf4742476532CA2387Bcc1631Af21C10c79";

  const handlePrepare = async () => {
    if (!walletClient || !publicClient) {
      onError?.("Wallet not connected");
      return;
    }

    setCertifying(true);

    try {
      // Step 1: Call backend prepare endpoint
      setStep("Preparing certification...");
      console.log("[AdminCertify] Step 1: Preparing certification for request:", requestId);
      
      const response = await adminApi.prepareCertification(
        requestId,
        adminKey,
        walletAddress
      );
      
      console.log("[AdminCertify] Backend response:", response);
      
      if (!response.success) {
        throw new Error(response.error || "Failed to prepare certification");
      }
      
      // La risposta ha i dati direttamente, non dentro certificationData
      const certificationData = {
        submissionId: response.submissionId,
        companyName: response.companyName,
        walletAddress: response.companyWallet,  // companyWallet -> walletAddress
        reportCID: response.reportCID,
        pdfCID: response.pdfCID,
        issuedAt: response.issuedAt,
        expiresAt: response.expiresAt,
        scoresSummary: response.scoresSummary,
        ipfsGatewayUrls: response.ipfsGatewayUrls
      };
      
      setCertData(certificationData);
      
      console.log("[AdminCertify] Certification prepared:", certificationData);
      
      setBackendScoringLoaded(false);
      setBackendScoringError("");
      setScoringData(null);
      setScoringStatus(null);

      try {
        setStep("Fetching scoring data...");
        const scoringResponse = await adminApi.getScoring(requestId, adminKey, walletAddress);
        console.log("[AdminCertify] Scoring data fetched:", scoringResponse);

        const fetchedTotals = extractBestTotals(scoringResponse);
        if (fetchedTotals) setScoringData(fetchedTotals);

        const fetchedStatus = extractScoringStatus(scoringResponse);
        if (fetchedStatus) setScoringStatus(fetchedStatus);

        if (!fetchedTotals) {
          setBackendScoringError("Backend scoring trovato ma senza totals/byQuestion validi.");
        }

        setBackendScoringLoaded(true);
      } catch (err) {
        console.warn("[AdminCertify] Could not fetch scoring data:", err.message);
        setBackendScoringLoaded(false);
        setBackendScoringError(err.message || "Impossibile leggere scoring dal backend");
      }
      
      // Show confirmation dialog
      setShowConfirmDialog(true);
      setStep("");
      setCertifying(false);

    } catch (error) {
      console.error("[AdminCertify] Error preparing:", error);
      setStep("");
      setCertifying(false);
      
      let errorMessage = "Failed to prepare certification";
      if (error.message) {
        errorMessage = error.message;
      }
      
      onError?.(errorMessage);
    }
  };

  const handleCertify = async () => {
    if (!walletClient || !publicClient || !certData) {
      onError?.("Missing required data");
      return;
    }

    setShowConfirmDialog(false);
    setCertifying(true);

    try {
      // Step 2: Sign and send on-chain transaction
      setStep("Waiting for wallet signature...");
      console.log("[AdminCertify] Step 2: Calling certifyCompany on-chain");
      console.log("[AdminCertify] Contract:", APPLETREE_CONTRACT);
      console.log("[AdminCertify] Company wallet:", certData.walletAddress);

      const { request } = await publicClient.simulateContract({
        address: APPLETREE_CONTRACT,
        abi: AppleTreeABI,
        functionName: "certifyCompany",
        args: [certData.walletAddress],
        account: walletAddress,
      });

      const txHash = await walletClient.writeContract(request);
      console.log("[AdminCertify] Transaction sent:", txHash);

      // Step 3: Wait for transaction receipt confirmation
      setStep("Waiting for transaction confirmation...");
      console.log("[AdminCertify] Step 3: Waiting for transaction receipt");
      
      const receipt = await publicClient.waitForTransactionReceipt({ 
        hash: txHash,
        confirmations: 1
      });

      console.log("[AdminCertify] Transaction confirmed:", receipt);

      // Step 4: Call backend confirm endpoint
      setStep("Confirming certification...");
      console.log("[AdminCertify] Step 4: Confirming certification on backend");
      
      const confirmResponse = await adminApi.confirmCertification(
        requestId,
        txHash,
        adminKey,
        walletAddress
      );

      if (!confirmResponse.success) {
        throw new Error(confirmResponse.error || "Failed to confirm certification");
      }

      console.log("[AdminCertify] Certification completed successfully!");
      
      // Step 5: Update UI state
      setStep("Certified ✅");
      
      onSuccess?.({
        ...confirmResponse.certification,
        certificationData: certData,
        receipt
      });

    } catch (error) {
      console.error("[AdminCertify] Error:", error);
      setStep("");
      
      let errorMessage = "Certification failed";
      if (error.message?.includes("User rejected")) {
        errorMessage = "Transaction rejected by user";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      onError?.(errorMessage);
    } finally {
      setCertifying(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setCertData(null);
    setStep("");
    setBackendScoringLoaded(false);
    setBackendScoringError("");
  };

  const canCertify = backendScoringLoaded && !!scoringData && scoringStatus === "SCORED";

  return (
    <div style={styles.container}>
      <button
        onClick={handlePrepare}
        disabled={certifying || !walletClient}
        style={{
          ...styles.button,
          opacity: certifying || !walletClient ? 0.6 : 1,
          cursor: certifying || !walletClient ? "not-allowed" : "pointer",
        }}
      >
        {certifying ? (
          <>
            <span style={styles.spinner}>⏳</span>
            {step || "Processing..."}
          </>
        ) : (
          <>
            <span style={styles.icon}>🔐</span>
            Certify On-Chain
          </>
        )}
      </button>

      {!walletClient && (
        <div style={styles.warning}>
          ⚠️ Please connect your wallet to certify
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && certData && (
        <div style={styles.dialogOverlay}>
          <div style={styles.dialog}>
            <h3 style={styles.dialogTitle}>🔐 Confirm Blockchain Certification</h3>
            
            <div style={styles.dialogContent}>
              <div style={styles.dialogRow}>
                <strong>Company:</strong>
                <span>{certData.companyName}</span>
              </div>
              <div style={styles.dialogRow}>
                <strong>Wallet Address:</strong>
                <span style={styles.address}>{certData.walletAddress}</span>
              </div>
              <div style={styles.dialogRow}>
                <strong>Submission ID:</strong>
                <span style={styles.address}>{certData.submissionId}</span>
              </div>
              <div style={styles.dialogRow}>
                <strong>Report IPFS CID:</strong>
                <span style={styles.address}>{certData.reportCID}</span>
              </div>
              <div style={styles.dialogRow}>
                <strong>PDF IPFS CID:</strong>
                <span style={styles.address}>{certData.pdfCID}</span>
              </div>
              {certData.ipfsGatewayUrls && (
                <>
                  <div style={styles.dialogRow}>
                    <strong>JSON Report:</strong>
                    <a 
                      href={certData.ipfsGatewayUrls.json} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={styles.link}
                    >
                      View JSON
                    </a>
                  </div>
                  <div style={styles.dialogRow}>
                    <strong>PDF Report:</strong>
                    <a 
                      href={certData.ipfsGatewayUrls.pdf} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={styles.link}
                    >
                      View PDF
                    </a>
                  </div>
                </>
              )}
              {scoringData && (
                <div style={styles.scoringSection}>
                  <div style={styles.dialogRow}>
                    <strong>ESG Scores (from backend):</strong>
                  </div>
                  <div style={styles.scoringGrid}>
                    <div style={styles.scoreItem}>
                      <span style={styles.scoreLabel}>Environmental (E):</span>
                      <span style={styles.scoreValue}>
                        {scoringData?.E ?? 0} / 40
                      </span>
                    </div>
                    <div style={styles.scoreItem}>
                      <span style={styles.scoreLabel}>Social (S):</span>
                      <span style={styles.scoreValue}>
                        {scoringData?.S ?? 0} / 35
                      </span>
                    </div>
                    <div style={styles.scoreItem}>
                      <span style={styles.scoreLabel}>Governance (G):</span>
                      <span style={styles.scoreValue}>
                        {scoringData?.G ?? 0} / 25
                      </span>
                    </div>
                    <div style={{...styles.scoreItem, ...styles.totalScore}}>
                      <span style={styles.scoreLabel}>TOTAL:</span>
                      <span style={{...styles.scoreValue, fontWeight: "800", fontSize: "16px"}}>
                        {scoringData?.total ?? 0} / 100
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {!scoringData && (
                <div style={styles.scoringWarning}>
                  ⚠️ <strong>Backend scoring non disponibile.</strong> {backendScoringError || "Impossibile mostrare i punteggi certificabili."}
                </div>
              )}
              <div style={styles.dialogRow}>
                <strong>Issued At:</strong>
                <span>{formatEnglishDate(certData.issuedAt)}</span>
              </div>
              <div style={styles.dialogRow}>
                <strong>Expires At:</strong>
                <span>{formatEnglishDate(certData.expiresAt)}</span>
              </div>
            </div>

            {scoringStatus && scoringStatus !== "SCORED" && (
              <div style={styles.scoringWarning}>
                ⚠️ <strong>Warning:</strong> Scoring not finalized (Status: {scoringStatus}). 
                Certification is blocked until backend status is SCORED.
              </div>
            )}

            <p style={styles.dialogWarning}>
              ⚠️ You will be asked to sign a blockchain transaction. 
              This will permanently certify this company on-chain.
            </p>

            <div style={styles.dialogActions}>
              <button
                onClick={handleCancel}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleCertify}
                style={styles.confirmButton}
                disabled={!canCertify}
                title={!canCertify ? "Serve scoring backend valido con status SCORED" : ""}
              >
                Sign & Certify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    marginTop: "20px",
  },
  button: {
    padding: "12px 24px",
    backgroundColor: "#8e44ad",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    justifyContent: "center",
  },
  icon: {
    fontSize: "18px",
  },
  spinner: {
    animation: "spin 1s linear infinite",
  },
  warning: {
    marginTop: "10px",
    padding: "10px",
    backgroundColor: "#fff3cd",
    color: "#856404",
    borderRadius: "4px",
    fontSize: "13px",
  },
  dialogOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  dialog: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "600px",
    width: "90%",
    maxHeight: "80vh",
    overflow: "auto",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
  },
  dialogTitle: {
    margin: "0 0 20px 0",
    fontSize: "20px",
    color: "#333",
    borderBottom: "2px solid #8e44ad",
    paddingBottom: "10px",
  },
  dialogContent: {
    marginBottom: "20px",
  },
  dialogRow: {
    marginBottom: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  address: {
    fontSize: "13px",
    fontFamily: "monospace",
    wordBreak: "break-all",
    color: "#666",
  },
  link: {
    color: "#8e44ad",
    textDecoration: "none",
    fontWeight: "600",
  },
  dialogWarning: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "12px",
    borderRadius: "6px",
    fontSize: "14px",
    marginBottom: "20px",
  },
  scoringWarning: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "12px",
    borderRadius: "6px",
    fontSize: "14px",
    marginBottom: "15px",
    border: "2px solid #ffc107",
  },
  scoringSection: {
    marginBottom: "15px",
    padding: "15px",
    backgroundColor: "#e8f5e9",
    borderRadius: "8px",
    border: "2px solid #27ae60",
  },
  scoringGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "10px",
    marginTop: "10px",
  },
  scoreItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "white",
    borderRadius: "6px",
  },
  totalScore: {
    gridColumn: "1 / -1",
    backgroundColor: "#d4edda",
  },
  scoreLabel: {
    fontSize: "13px",
    color: "#495057",
    fontWeight: "600",
  },
  scoreValue: {
    fontSize: "14px",
    color: "#2c3e50",
    fontWeight: "700",
  },
  dialogActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  },
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  confirmButton: {
    padding: "10px 20px",
    backgroundColor: "#8e44ad",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s",
  },
};

export default AdminCertifyButton;
